import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import MuiTooltip from '@mui/material/Tooltip';
import { muiGlobalTheme } from '@themes';
import { cloneDeep } from 'lodash';
import { colors } from '../../css';
import { StyledButton, ModalContent } from './ToolTip.styles';
import { SVGIcon } from '../SVGIcon';
import { Adaptive } from '../../Utility/Adaptive';
import { Modal } from '../../Utility/Modal';
import { Grid } from '../../Utility/Grid';
import { RichText } from '../../Utility/RichText';
import { Text } from '../Text';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

/** Converter setup for RichText */
const toolTipHeadingConverter = {
  element: Text.HeadingFifth,
  props: { color: 'secondary', tagName: 'h4' }
};
const toolTipParaConverter = {
  element: Text.CopyMini,
  props: { color: 'secondary', tagName: 'p' }
};

const modalHeadingConverter = {
  element: Text.HeadingFifth,
  props: { color: 'primary', tagName: 'h4' }
};
const modalParaConverter = {
  element: Text.CopyParagraph,
  props: { color: 'primary', tagName: 'p' }
};

const customizeMuiGlobalTheme = (styledProps) => {
  const { right = '' } = styledProps;
  const newToolTipTheme = cloneDeep(muiGlobalTheme);
  newToolTipTheme.components.MuiTooltip = {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.colorGrey02,
        borderRadius: 0,
        position: 'relative',
        fontSize: 'inherit',
        padding: '22px',
        maxWidth: 450,
        '&:before': {
          content: `''`,
          display: 'block',
          height: 0,
          width: 0,
          border: '5px solid transparent',
          position: 'absolute'
        }
      },
      tooltipPlacementLeft: {
        '&:before': {
          transform: 'rotate(45deg) translateY(-50%)',
          top: '50%',
          right: '0',
          borderTop: `5px solid ${colors.colorGrey02}`,
          borderRight: `5px solid ${colors.colorGrey02}`
        }
      },
      /* Styles applied to the tooltip (label wrapper) element if `placement` contains "right". */
      tooltipPlacementRight: {
        '&:before': {
          transform: 'rotate(-45deg) translateY(-50%)',
          top: '50%',
          left: '0',
          borderTop: `5px solid ${colors.colorGrey02}`,
          borderLeft: `5px solid ${colors.colorGrey02}`
        }
      },
      /* Styles applied to the tooltip (label wrapper) element if `placement` contains "top". */
      tooltipPlacementTop: {
        '&:before': {
          transform: 'rotate(135deg) translateX(-50%)',
          top: 'calc(100% - 2px)',
          right: right || '50%',
          borderTop: `5px solid ${colors.colorGrey02}`,
          borderRight: `5px solid ${colors.colorGrey02}`
        }
      },
      /* Styles applied to the tooltip (label wrapper) element if `placement` contains "bottom". */
      tooltipPlacementBottom: {
        '&:before': {
          transform: ' translateX(50%) rotate(45deg)',
          bottom: 'calc(100% - 5px)',
          right: right || '50%',
          borderTop: `5px solid ${colors.colorGrey02}`,
          borderLeft: `5px solid ${colors.colorGrey02}`
        }
      }
    }
  };
  return createTheme(newToolTipTheme);
};
function ToolTip(props) {
  const { children, className, content, disableModal, styledProps } = props;

  const [open, setOpen] = useState(false);

  const toolTipTheme = useRef(customizeMuiGlobalTheme(styledProps));

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const toolTip = (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={toolTipTheme.current}>
        <MuiTooltip
          title={
            <RichText
              content={content}
              converters={{
                h1: toolTipHeadingConverter,
                h2: toolTipHeadingConverter,
                h3: toolTipHeadingConverter,
                h4: toolTipHeadingConverter,
                h5: toolTipHeadingConverter,
                h6: toolTipHeadingConverter,
                p: toolTipParaConverter,
                strong: toolTipParaConverter
              }}
            />
          }
        >
          <StyledButton className={className} type="button">
            {children}
          </StyledButton>
        </MuiTooltip>
      </ThemeProvider>
    </StyledEngineProvider>
  );

  const modal = (
    <>
      <StyledButton className={className} type="button" onClick={openModal}>
        {children}
      </StyledButton>
      <Modal open={open} handleClose={closeModal} width="100%" height="100vh">
        <ModalContent>
          <Grid.Container>
            <Grid.Row>
              <Grid.Col>
                <RichText
                  content={content}
                  converters={{
                    h1: modalHeadingConverter,
                    h2: modalHeadingConverter,
                    h3: modalHeadingConverter,
                    h4: modalHeadingConverter,
                    h5: modalHeadingConverter,
                    h6: modalHeadingConverter,
                    p: modalParaConverter,
                    strong: modalParaConverter
                  }}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid.Container>
        </ModalContent>
      </Modal>
    </>
  );

  return <Adaptive sm={!disableModal ? modal : toolTip} md={toolTip} />;
}

ToolTip.propTypes = {
  /** trigger for tooltip (or modal on sm) to open (text, icon, asterisk etc.) */
  children: ReactChildrenPropType,
  /** Allows you to style component in styled components file */
  className: PropTypes.string,
  /** Content to display inside the tooltip */
  content: PropTypes.string,
  /** Turn off modal so xs/sm use tooltip instead */
  disableModal: PropTypes.bool,
  styledProps: PropTypes.shape({ right: PropTypes.string })
};

ToolTip.defaultProps = {
  children: <SVGIcon selectedIcon="info" />,
  className: null,
  content: null,
  disableModal: false,
  styledProps: { right: '' }
};

export default ToolTip;
